// ── Generic (non-image) file attachments (C1447) ──
//
// Web-SPA mirror of ai/todo/server/src/client/file-attach.js — same task_files store
// (api/src/routes/files.js), same client-side rules. Keep ALLOWED_MIME/EXT_MIME_FALLBACK/
// FILE_ACCEPT/MAX_FILE_BYTES in sync across all three copies (this file, the Task App's
// file-attach.js, and the API's FILE_MIME_EXT/MAX_FILE_BYTES) — one contract, diverge in
// all three or none. Split out of image-paste.js/utils.js for the same reason
// image-paste.js itself is a lib/ module: this needs both `showToast` (ui.js) and
// `escapeHtml`-adjacent helpers (utils.js), and ui.js already imports from utils.js, so
// nothing that needs both can live in utils.js without an import cycle.

import { api } from '../api.js';
import { insertAtCursor } from '../utils.js';
import { showToast } from '../ui.js';

export const MAX_FILE_BYTES = 1024 * 1024; // 1 MB — mirrors api/src/routes/files.js MAX_FILE_BYTES

// Mirrors api/src/routes/files.js FILE_MIME_EXT — the API is the real enforcement point,
// this is only for a fast, friendly client-side reject before spending a round trip.
const ALLOWED_MIME = new Set([
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.oasis.opendocument.text',
  'application/vnd.oasis.opendocument.spreadsheet',
  'text/plain',
  'text/markdown',
  'text/csv',
  'text/tab-separated-values',
  'application/json',
  'application/xml',
  'text/xml',
  'application/yaml',
  'text/yaml',
  'application/rtf',
  'text/rtf',
]);

// Browsers frequently report file.type === '' for plain-text-ish dev files (macOS reports
// no type for .log/.diff/.patch/.sql/.yml/.ini/.conf). Fall back to extension when that
// happens — same fallback the Task App's file-attach.js uses.
const EXT_MIME_FALLBACK = {
  txt: 'text/plain', log: 'text/plain', diff: 'text/plain', patch: 'text/plain',
  sql: 'text/plain', ini: 'text/plain', conf: 'text/plain',
  md: 'text/markdown', markdown: 'text/markdown',
  csv: 'text/csv', tsv: 'text/tab-separated-values',
  json: 'application/json',
  xml: 'application/xml',
  yml: 'text/yaml', yaml: 'text/yaml',
  rtf: 'application/rtf',
};

export const FILE_ACCEPT = [
  '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.odt', '.ods',
  '.txt', '.md', '.csv', '.tsv', '.json', '.xml', '.yaml', '.yml', '.rtf',
  '.log', '.diff', '.patch', '.sql', '.ini', '.conf',
].join(',');

export function resolveFileMime(file) {
  if (file.type && ALLOWED_MIME.has(file.type)) return file.type;
  const ext = String(file.name || '').split('.').pop().toLowerCase();
  return EXT_MIME_FALLBACK[ext] || null;
}

function readFileBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(',')[1] || '');
    r.onerror = () => reject(new Error('Read failed'));
    r.readAsDataURL(file);
  });
}

// `taskKey` may be a plain value OR a zero-arg fn returning the current value — same
// thunk contract as lib/image-paste.js's attachImageHandlers, so a file picked before an
// async task_key reservation resolves (board.js's create-task modal) still stamps
// task_files.task_key correctly.
function resolveTaskKey(k) {
  return typeof k === 'function' ? k() : k;
}

// Routes through the `api` client (not a raw fetch, unlike utils.js's uploadImage) so a
// stale/expired token gets apiFetch's 401 -> reauth retry instead of silently failing.
export async function uploadFile(projectId, file, taskKey = null) {
  const mimeType = resolveFileMime(file);
  if (!mimeType) {
    const err = new Error('Unsupported file type');
    err.code = 'INVALID_MIME';
    throw err;
  }
  if (!file.size || file.size > MAX_FILE_BYTES) {
    const err = new Error('File too large (max 1 MB)');
    err.code = 'TOO_LARGE';
    throw err;
  }
  const data = await readFileBase64(file);
  return api.post(`/projects/${projectId}/files`, {
    filename: file.name,
    mimeType,
    data,
    taskKey: resolveTaskKey(taskKey),
  });
}

// uploadAttachmentFile(textarea, file, {projectId, taskKey, onUpload}) — mirrors
// image-paste.js's uploadAndInsert: placeholder inserted immediately at the cursor,
// swapped for the real markdown link ref on success, stripped on failure. Client-side
// size/MIME checks run BEFORE the placeholder is inserted, so a rejected file leaves the
// description untouched. onUpload({kind:'files', url}) — optional, see
// image-paste.js's uploadImageFile() for the same contract; default no-op.
export async function uploadAttachmentFile(textarea, file, { projectId, taskKey = null, onUpload = null } = {}) {
  const mimeType = resolveFileMime(file);
  if (!mimeType) {
    showToast('Unsupported file type', 'error');
    return;
  }
  if (!file.size || file.size > MAX_FILE_BYTES) {
    showToast('File too large (max 1 MB)', 'error');
    return;
  }
  const placeholder = `[uploading ${file.name}...]()`;
  insertAtCursor(textarea, placeholder);
  try {
    const r = await uploadFile(projectId, file, taskKey);
    // Upload can outlive the editor (modal closed / description saved mid-flight) —
    // don't write into a detached textarea.
    if (!textarea.isConnected) return;
    const name = r?.filename || file.name;
    textarea.value = textarea.value.replace(placeholder, `[${name}](${r.url})`);
    textarea.dispatchEvent(new Event('input', { bubbles: true }));
    if (onUpload) onUpload({ kind: 'files', url: r.url });
  } catch (err) {
    if (textarea.isConnected) {
      textarea.value = textarea.value.replace(placeholder, '');
      textarea.dispatchEvent(new Event('input', { bubbles: true }));
    }
    showToast(err.message || 'Upload failed', 'error');
  }
}
